import { hoursToMinutes, minutesToHours } from '@acme/common'
import { SN } from '@acme/core'
import { GridCellParams } from '@/global/components/DataGrid/utils/types'
import { useQuery } from 'react-query'
import { Registro } from 'service/api'
import { saldoHorasV2 } from 'service/api/endpoints/saldoHoras'
import { useStore } from 'store'
import { match } from 'ts-pattern'

/** Mapa `data (YYYY-MM-DD)` → saldo do dia em minutos (com sinal). */
export type SaldoDiarioMap = Record<string, number>

/** Referência estável p/ quando não há mapa (evita recomputar memos). */
const EMPTY_SALDO_MAP: SaldoDiarioMap = {}

/**
 * Busca o mapa `data -> saldoDiaMinutos` da tabela `saldo_horas_diario` para o
 * funcionário/competência atualmente selecionados (view mensal).
 *
 * Só dispara quando a empresa tem `exibirSaldoDiario` ligado, há funcionário
 * selecionado e competência definida. Fora disso retorna `{}` e o
 * `SaldoDiarioCell` cai no cálculo client-side (extras − faltas).
 */
export function useSaldoDiarioMap(enabled: boolean): SaldoDiarioMap {
    const empresa = useStore(state => state.company)
    const funcionarioId = useStore(state => state.employeeId)
    const competencia = useStore(state => state.competencia)

    const shouldFetch =
        enabled &&
        SN.isTrue(empresa?.exibirSaldoDiario) &&
        !!empresa?.id &&
        funcionarioId > 0 &&
        !!competencia

    const { data } = useQuery(
        [
            'saldo-horas',
            'v2',
            'diario-map',
            empresa?.id,
            funcionarioId,
            competencia,
        ],
        () =>
            saldoHorasV2.get<SaldoDiarioMap>('diario-map', {
                empresaId: empresa!.id,
                funcionarioId,
                competencia,
            }),
        {
            enabled: shouldFetch,
            staleTime: 60_000,
        },
    )

    return data ?? EMPTY_SALDO_MAP
}

export const SaldoDiarioCell: React.FC<
    GridCellParams<Registro[]> & { saldoMap?: SaldoDiarioMap }
> = ({ row, saldoMap }) => {
    // Preferência: saldo puro da tabela (por data). Fallback: cálculo
    // client-side (extras − faltas). `?? ` preserva o valor 0 da tabela.
    const saldo =
        saldoMap?.[row.dia] ??
        hoursToMinutes(row.horasExtra) - hoursToMinutes(row.faltas)

    return match(saldo)
        .when(
            x => x === 0,
            () => <span className="text-muted-foreground">+00:00</span>,
        )
        .when(
            x => x < 0,
            x => (
                <span className="font-semibold text-error">
                    -{minutesToHours(Math.abs(x))}
                </span>
            ),
        )
        .otherwise(x => (
            <span className="font-semibold text-success">
                +{minutesToHours(x)}
            </span>
        ))
}
